// /**
//  * utils.ts — shared helpers (cn, formatters, misc).
//  *
//  * `cn` is the standard shadcn/ui class-merge helper used by every UI primitive.
//  */
// import { clsx, type ClassValue } from "clsx";
// import { twMerge } from "tailwind-merge";

// export function cn(...inputs: ClassValue[]): string {
//   return twMerge(clsx(inputs));
// }

// /** Format a number as USD currency (no cents for large values). */
// export function formatCurrency(value: number, cents = false): string {
//   return new Intl.NumberFormat("en-US", {
//     style: "currency",
//     currency: "USD",
//     maximumFractionDigits: cents ? 2 : 0,
//   }).format(value ?? 0);
// }

// /** Format a 0–1 ratio as a percentage string. */
// export function formatPercent(ratio: number, digits = 1): string {
//   return `${((ratio ?? 0) * 100).toFixed(digits)}%`;
// }

// /** Format an ISO timestamp as a short date. */
// export function formatDate(iso: string | null | undefined): string {
//   if (!iso) return "—";
//   const d = new Date(iso);
//   if (Number.isNaN(d.getTime())) return "—";
//   return d.toLocaleDateString("en-US", {
//     year: "numeric",
//     month: "short",
//     day: "numeric",
//   });
// }

// /** Format an ISO timestamp as date + time. */
// export function formatDateTime(iso: string | null | undefined): string {
//   if (!iso) return "—";
//   const d = new Date(iso);
//   if (Number.isNaN(d.getTime())) return "—";
//   return d.toLocaleString("en-US", {
//     month: "short",
//     day: "numeric",
//     hour: "2-digit",
//     minute: "2-digit",
//   });
// }

// /** Relative time, e.g. "3h ago". */
// export function timeAgo(iso: string | null | undefined): string {
//   if (!iso) return "—";
//   const d = new Date(iso).getTime();
//   if (Number.isNaN(d)) return "—";
//   const diff = Date.now() - d;
//   const mins = Math.round(diff / 60000);
//   if (mins < 1) return "just now";
//   if (mins < 60) return `${mins}m ago`;
//   const hrs = Math.round(mins / 60);
//   if (hrs < 24) return `${hrs}h ago`;
//   const days = Math.round(hrs / 24);
//   if (days < 30) return `${days}d ago`;
//   return formatDate(iso);
// }

// /** Truncate a string to `n` chars with ellipsis. */
// export function truncate(s: string, n: number): string {
//   if (!s) return "";
//   return s.length > n ? `${s.slice(0, n - 1)}…` : s;
// }

// /** Initials from a name/email for avatars. */
// export function initials(name: string | null | undefined): string {
//   if (!name) return "?";
//   const parts = name.trim().split(/\s+/);
//   if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
//   return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
// }

/**
 * utils.ts — shared helpers (cn, formatters, misc).
 *
 * `cn` is the standard shadcn/ui class-merge helper used by every UI primitive.
 */
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Format a number as USD currency (no cents for large values). */
export function formatCurrency(value: number, cents = false): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: cents ? 2 : 0,
  }).format(value ?? 0);
}

/** Format a 0–1 ratio as a percentage string. */
export function formatPercent(ratio: number, digits = 1): string {
  return `${((ratio ?? 0) * 100).toFixed(digits)}%`;
}

/** Format an ISO timestamp as a short date. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Format an ISO timestamp as date + time. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Relative time, e.g. "3h ago". */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "—";
  const diff = Date.now() - d;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}

/** Truncate a string to `n` chars with ellipsis. */
export function truncate(s: string, n: number): string {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

/** Initials from a name/email for avatars. */
export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Convert a raw enum/snake_case/camelCase string to Proper Case for display.
 *
 * Examples:
 *   "active"        → "Active"
 *   "qa_passed"     → "Qa Passed"   (use explicit label maps for these)
 *   "QaPassed"      → "Qa Passed"
 *   "reply rate"    → "Reply Rate"
 *   "FUNDING_URGENCY" → "Funding Urgency"
 *
 * Prefer explicit label maps (SEQ_STATUS_LABEL etc.) for known enums.
 * Use this as a safe fallback for unknown values.
 */
export function toProperCase(str: string | null | undefined): string {
  if (!str) return "—";
  return str
    // Insert space before uppercase letters that follow lowercase (camelCase split)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    // Replace underscores and hyphens with spaces
    .replace(/[_-]+/g, " ")
    // Capitalise the first letter of each word, lowercase the rest
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
